import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { useSearchParams } from "react-router-dom";

export default function AdminReferrals() {
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";
  const [analytics, setAnalytics] = React.useState({
    summary: { sitewideTotal: 0, filteredUniqueTotal: 0, filteredRefCodes: 0 },
    items: [],
    topReferrers: [],
    timeline: [],
    pagination: { page: 1, pageSize: 12, total: 0, pages: 1 },
  });
  const [search, setSearch] = React.useState("");
  const [fromDate, setFromDate] = React.useState("");
  const [toDate, setToDate] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const fetchAnalytics = React.useCallback(async () => {
    if (!passcode) return;
    setLoading(true);
    try {
      const { getAdminReferralAnalytics } = await import("../../lib/api");
      const data = await getAdminReferralAnalytics({
        passcode,
        search,
        fromDate,
        toDate,
        page,
        pageSize: 12,
      });
      setAnalytics(data || {
        summary: { sitewideTotal: 0, filteredUniqueTotal: 0, filteredRefCodes: 0 },
        items: [],
        topReferrers: [],
        timeline: [],
        pagination: { page: 1, pageSize: 12, total: 0, pages: 1 },
      });
      setError("");
    } catch (e) {
      setError("Failed to fetch referral analytics. Check passcode and filters.");
    } finally {
      setLoading(false);
    }
  }, [passcode, search, fromDate, toDate, page]);

  React.useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const removeRef = async (ref, count) => {
    const ok = window.confirm(`Remove referral '${ref}' and its ${count} user${count === 1 ? '' : 's'}?`);
    if (!ok) return;
    try {
      const { deleteAdminReferralRef } = await import("../../lib/api");
      await deleteAdminReferralRef(ref, passcode);
      fetchAnalytics();
    } catch (e) {
      setError("Failed to remove referral.");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10 space-y-6" data-testid="admin-referrals-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100" data-testid="admin-referrals-title">Referral Analytics</CardTitle>
        </CardHeader>
        <div className="text-sm text-slate-500 px-6" data-testid="admin-referrals-subtitle">Use Counters for site/intro/week visits. This page shows unique referred visitors only.</div>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="admin-referrals-actions-row">
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search referral code" className="px-3 py-1.5 text-sm rounded border min-w-[220px]" data-testid="admin-referrals-search-input" />
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} className="px-3 py-1.5 text-sm rounded border" data-testid="admin-referrals-from-date-input" />
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} className="px-3 py-1.5 text-sm rounded border" data-testid="admin-referrals-to-date-input" />
            <button className="px-3 py-1.5 text-xs rounded border" onClick={resetFilters} data-testid="admin-referrals-reset-filters-button">Reset Filters</button>
          </div>
          {!passcode && <div className="text-sm text-slate-500">Missing passcode. Go to /admin to generate links.</div>}
          {loading && <div className="text-sm">Loading…</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-referrals-summary-cards">
                <StatCard label="All-time referred users (all codes)" helpText="Total unique users referred across the whole site." value={analytics.summary.sitewideTotal} testId="admin-referrals-sitewide-total" />
                <StatCard label="Referrals listed" helpText="Unique referred users matching search/date filters." value={analytics.summary.filteredUniqueTotal} testId="admin-referrals-filtered-total" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4">
                <div>
                  <h3 className="text-sm font-semibold mb-1" data-testid="admin-referrals-code-list-title">Referral code results</h3>
                  <p className="text-xs text-slate-500 mb-2" data-testid="admin-referrals-code-list-help">Each row is one referral code and how many unique users it brought.</p>
                  <div className="divide-y divide-slate-200 dark:divide-slate-800 border rounded-lg">
                    {(analytics.items || []).length === 0 && (
                      <div className="py-6 px-3 text-sm text-slate-500">No referral data for current filters.</div>
                    )}
                    {(analytics.items || []).map((item) => (
                      <div key={item.ref} className="py-2 px-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium" data-testid={`admin-referrals-ref-code-${item.ref}`}>{item.ref}</div>
                          <div className="text-xs text-slate-500" data-testid={`admin-referrals-last-seen-${item.ref}`}>
                            Last seen: {item.lastSeenAt ? new Date(item.lastSeenAt).toLocaleString() : "-"}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-sm font-medium" data-testid={`admin-referrals-ref-count-${item.ref}`}>{item.count}</div>
                          <button className="px-2 py-1 text-xs rounded border" onClick={() => removeRef(item.ref, item.count)} data-testid={`admin-referrals-remove-${item.ref}`}>Remove code</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center justify-between" data-testid="admin-referrals-pagination-row">
                    <div className="text-xs text-slate-500" data-testid="admin-referrals-page-info">Result page {analytics.pagination.page} of {analytics.pagination.pages}</div>
                    <div className="flex items-center gap-2">
                      <button className="px-2 py-1 text-xs rounded border" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={analytics.pagination.page <= 1} data-testid="admin-referrals-prev-page-button">Prev</button>
                      <button className="px-2 py-1 text-xs rounded border" onClick={() => setPage((p) => Math.min(analytics.pagination.pages || 1, p + 1))} disabled={analytics.pagination.page >= (analytics.pagination.pages || 1)} data-testid="admin-referrals-next-page-button">Next</button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="border rounded-lg p-3" data-testid="admin-referrals-top-referrers-card">
                    <h3 className="text-sm font-semibold mb-1">Top referral codes</h3>
                    <p className="text-xs text-slate-500 mb-2" data-testid="admin-referrals-top-referrers-help">Highest-performing referral codes by unique users.</p>
                    <div className="space-y-2">
                      {(analytics.topReferrers || []).length === 0 && <div className="text-xs text-slate-500">No data yet.</div>}
                      {(analytics.topReferrers || []).map((it, idx) => (
                        <div key={`${it.ref}-${idx}`} className="flex items-center justify-between text-sm">
                          <span data-testid={`admin-referrals-top-ref-${idx}`}>{it.ref}</span>
                          <span className="font-semibold" data-testid={`admin-referrals-top-ref-count-${idx}`}>{it.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, helpText, value, testId }) {
  return (
    <div className="rounded-lg border p-3" data-testid={testId}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-[11px] text-slate-500 mt-1" data-testid={`${testId}-help`}>{helpText}</div>
      <div className="mt-1 text-2xl font-semibold">{value ?? 0}</div>
    </div>
  );
}